import Cookies from 'js-cookie';
import { stopSubmit } from 'redux-form';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';

import * as authActions from '~/actions/auth.actions';
import { ActionKeys } from '~/actions/constants';
import { type MarketoForms2 } from '~/components/Marketo';
import { USER_REGISTER_SUCCESS_COOKIE_NAME } from '~/constants/cookies';
import { CSV_EXPORT_RESULTS_KEY } from '~/constants/local-storage-keys';
import { OnboardingLocalStorageKeys } from '~/modules/onboarding/types/onboarding.type';
import { setOnboardingInProgress } from '~/modules/onboarding/utils/onboarding.util';
import { getEmbeddableUser } from '~/pages/common/EmbeddableAuth';
import { getParsedQS } from '~/selectors/router.selectors';
import * as authService from '~/services/auth.service';
import * as RegisterUser from '~/types/RegisterUser';
import { saveInLocalStorage } from '~/utils/local-storage';
import { getLocale } from '~/utils/locale';
import { setAttributionCookie } from '~/utils/marketing-source-attribution';
import { newRelic } from '~/utils/newRelic/newRelic';
import { extractMarketingPayloadFromCookie, getUTMCookie } from '~/utils/utm-tracking';

declare global {
  interface Window {
    MktoForms2: MarketoForms2; // No specific type
  }
}

const PLAYWRIGHT_TESTER_EMAIL = 'stester+playwright';

const preventMarketoTracking = ({ user }: RegisterUser.RegisterUser) =>
  CONFIG.environmentName !== 'production' || // Marketo form submit only for production
  user.email.startsWith(PLAYWRIGHT_TESTER_EMAIL);

export function* register(action: ReturnType<typeof authActions.register>) {
  try {
    localStorage.removeItem(CSV_EXPORT_RESULTS_KEY);
    let UTMCookies = getUTMCookie();

    // This cookie is set in GTM, if for some reason GTM is not loaded and the cookie is undefined
    // we try to set it again calling the source code instead of waiting on GTM.
    // Then, we get the cookie again
    if (!UTMCookies) {
      setAttributionCookie();
      UTMCookies = getUTMCookie();
    }

    UTMCookies = UTMCookies || {};

    const QS: Record<string, string> = yield select(getParsedQS);
    const pricePlan = getStartingPricePlan(QS);
    const marketingPayload = extractMarketingPayloadFromCookie(UTMCookies);
    const embeddableUser = getEmbeddableUser();

    const trackerSessionId = Cookies.get('tracker_sessionid') || '';

    const trackingPayload = {
      tracker_sessionid: trackerSessionId,
    };

    const newPayload: RegisterUser.RegisterUser = {
      ...action.payload,
      context: {
        fixedFeeWaivedUntil: QS.fixed_fee_waived_until,
        lang: QS.lang,
        locale: getLocale(),
        next: QS.next || '',
        ...action.payload.context,
        ...pricePlan,
      },
      marketing: {
        initialLandingPage: Cookies.get('initial_landing_page') || '',
        psPartnerKey: Cookies.get('ps_partner_key') || '',
        psXid: Cookies.get('ps_xid') || '',
        regMethod: embeddableUser ? 'embeddable' : 'web-responsive-3',
        registrationLandingPage: Cookies.get('registration_landing_page') || '',
        uspsReferralKey: QS.usps_referral_key || '',
        ...action.payload.marketing,
        ...marketingPayload,
      },
      userTracking: {
        ...trackingPayload,
      },
    };

    const response: RegisterUser.RegisterResponse = yield call(
      authService.register,
      RegisterUser.toJSON(newPayload),
    );

    yield call(Cookies.set, 'jwt', response.data.jwt, { domain: CONFIG.shippoRootUrl });
    yield call(Cookies.set, 'is_logged_in', 'true', { domain: CONFIG.shippoRootUrl });
    yield call(Cookies.set, 'copy', response.data.jwt, { domain: CONFIG.shippoRootUrl });

    // Wrapping this part in a try catch so that
    // any error here doesn't create an issue with signup
    try {
      if (!preventMarketoTracking(action.payload))
        yield call(marketoSubmit, action.payload.user, marketingPayload);
    } catch (error) {
      newRelic.noticeError('Failed to submit Marketo form', { error });
    }

    // If next is not in the url params, we just continue as normal
    // Otherwise, we redirect if the url is absolute, and prepend the Django base url if it's relative
    let nextUrl;
    if (
      !action.next ||
      QS.reg_partner === 'ebay' || // Users coming from ebay have a "next" param that gets them
      // redirected to django & then back to the web app, skipping onboarding steps.
      // We are going to ignore that here, so they can go through the normal onboarding flow.
      (newPayload.context && newPayload.context.plan === 'professional')
    ) {
      nextUrl = '/user/';
    } else {
      const absolute = new RegExp(/^https?:\/\//i);
      if (absolute.test(action.next)) nextUrl = action.next;
      else nextUrl = CONFIG.djangoBaseUrl + action.next;
    }

    yield put(authActions.registerSuccess(response.data.session_token, nextUrl));
    yield call(Cookies.set, USER_REGISTER_SUCCESS_COOKIE_NAME, 'true', {
      domain: CONFIG.shippoRootUrl,
    });

    // This determines if the user should be shown the onboarding
    if (!embeddableUser)
      yield call(saveInLocalStorage, OnboardingLocalStorageKeys.SHOULD_ONBOARD, 'true');

    yield put(authActions.finishRegister());
  } catch (err) {
    if (err) {
      // Don't show onboarding if there's an error
      yield call(setOnboardingInProgress, false);
      const error = err?.response?.data;
      const errorMap = {
        email: error?.email,
        errorMessage: error?.error || (err.toJSON && err.toJSON()?.message),
        firstName: error?.first_name,
        lastName: error?.last_name,
        password: error?.password,
      };
      yield put(authActions.registerError(errorMap));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yield put(stopSubmit('join', errorMap as any));
    }
  }
}

/**
 * Parses the QS object and prepares a pricing test object that correspond to register.context payload
 * @param QS query string object parsed from url
 */
export const getStartingPricePlan = (QS: { [key: string]: string }) => {
  const pricingTags = ['plan', 'tier', 'cycle', 'variant'];
  const pricePlan: Record<string, string> = {};

  pricingTags.forEach((tag) => {
    const qsValue = QS[tag];
    if (qsValue) {
      if (tag === 'variant') pricePlan['pricingTestVariant'] = qsValue;
      else pricePlan[tag] = qsValue;
    }
  });

  return pricePlan;
};

const withDefaultValue = (value, defaultValue) => (!value || value === '' ? defaultValue : value);

/**
 * Calls the Marketo form submit
 */
export const marketoSubmit = (
  user: RegisterUser.User,
  marketingPayload: RegisterUser.MarketingPayload,
) => {
  if (!window.MktoForms2) return;

  const mktoForm = window.MktoForms2.getForm(1194);

  if (!mktoForm) return;

  const { email, firstName, lastName } = user;
  const campaign = withDefaultValue(marketingPayload.campaign, 'none');
  const campaignContent = 'shippo_sign_up';
  const medium = withDefaultValue(marketingPayload.medium, 'none');
  const source = withDefaultValue(marketingPayload.source, 'none');
  const campaignTerm = withDefaultValue(marketingPayload.campaignTerm, 'none');

  mktoForm.addHiddenFields({
    // These are the values which will be submitted to Marketo
    Email: email,
    FirstName: firstName,
    Inbound_Type__c: 'Shippo Sign Up',
    LastName: lastName,
    eNV: process.env.NODE_ENV,
    fTutmcontent: campaignContent,
    fTutmterm: campaignTerm,
    ft_utm_campaign: campaign,
    ft_utm_medium: medium,
    ft_utm_source: source,
    utm_campaign: campaign,
    utm_content: campaignContent,
    utm_medium: medium,
    utm_source: source,
    utm_term: campaignTerm,
  });

  return new Promise<void>((resolve) => {
    newRelic.getInteraction()?.setName('MARKETO_FORM_SUBMIT').setAttribute('email', email).save();

    // the marketo form API doesn't offer an onError callback,
    // since submits should never fail (weirdness by design).
    // so we need to make sure that the promise always resolves,
    // even if there is an error.
    const timeoutId = setTimeout(resolve, 1000);

    mktoForm
      .onSuccess(() => {
        newRelic
          .getInteraction()
          ?.setName('MARKETO_FORM_SUBMIT_SUCCESS')
          .setAttribute('email', email)
          .save();

        clearTimeout(timeoutId);
        resolve();
      })
      .submit();
  });
};

/**
 * clearRegisterError saga function
 * to clear register error after 2 seconds
 * so that we can register the next error
 */
function* clearRegisterError() {
  yield delay(2000);
  yield put(authActions.clearAuthError());
}

export default [
  takeLatest(ActionKeys.REGISTER, register),
  takeLatest(ActionKeys.REGISTER_ERROR, clearRegisterError),
];
